/**
 * Peppol Report Aggregator
 *
 * Pure, side-effect-free helpers that turn a list of Peppol (IRSP / InvoiceNow)
 * submissions into the buckets/figures needed by the Peppol dashboard and
 * reports. Keeping these as pure functions makes them reusable across views and
 * straightforward to unit test, and lets the data source (client-side list
 * aggregation today, backend aggregation endpoints later) change without
 * touching the presentation layer.
 *
 * Expected submission shape (subset of GET /irsp-submissions list items):
 *   {
 *     transactionId, submissionDate, destination ('C3C5' | 'C5'),
 *     statusOverall ('Submitted' | 'Processing' | 'PartiallyCompleted' |
 *                    'Completed' | 'Error' | 'Rejected'),
 *     peppolStatus / irasStatus ('SUCC' | 'RJCT' | 'PEND' | null),
 *     docCount (number) | documents (array)
 *   }
 */
import { sgPeppolStatusOverall } from '@/constants/sg-peppol-statuses'

// Classification of overall submission statuses into reporting groups.
export const OVERALL_STATUS_GROUPS = {
  completed: ['Completed'],
  inProgress: ['Submitted', 'Processing', 'PartiallyCompleted'],
  errorsRejected: ['Error', 'Rejected'],
}

export const API_STATUS_KEYS = ['SUCC', 'RJCT', 'PEND']

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function num(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Number of documents represented by a submission. Prefers an explicit
 * docCount, falling back to the length of an embedded documents array.
 */
export function docCountOf(submission) {
  if (!submission) return 0
  if (submission.docCount != null) return num(submission.docCount)
  if (Array.isArray(submission.documents)) return submission.documents.length
  return 0
}

/** Sum of document counts across submissions. */
export function sumDocuments(submissions = []) {
  return submissions.reduce((total, s) => total + docCountOf(s), 0)
}

function inGroup(submission, group) {
  return OVERALL_STATUS_GROUPS[group].includes(submission && submission.statusOverall)
}

/** Count of submissions per overall status (keyed by status key). */
export function bucketByOverallStatus(submissions = []) {
  const buckets = {}
  sgPeppolStatusOverall.forEach((s) => {
    buckets[s.key] = 0
  })
  submissions.forEach((s) => {
    const key = s && s.statusOverall
    if (key && Object.prototype.hasOwnProperty.call(buckets, key)) {
      buckets[key] += 1
    } else {
      buckets.Unknown = (buckets.Unknown || 0) + 1
    }
  })
  return buckets
}

/**
 * Count of submissions per API status (SUCC/RJCT/PEND) for a given field,
 * e.g. 'peppolStatus' or 'irasStatus'. Missing values are bucketed as NA.
 */
export function bucketByApiStatus(submissions = [], field) {
  const buckets = { SUCC: 0, RJCT: 0, PEND: 0, NA: 0 }
  submissions.forEach((s) => {
    const value = s && s[field]
    if (value && Object.prototype.hasOwnProperty.call(buckets, value)) {
      buckets[value] += 1
    } else {
      buckets.NA += 1
    }
  })
  return buckets
}

/** Count of submissions per destination/route. */
export function bucketByRoute(submissions = []) {
  const buckets = {}
  submissions.forEach((s) => {
    const key = (s && s.destination) || 'Unknown'
    buckets[key] = (buckets[key] || 0) + 1
  })
  return buckets
}

/** High-level summary used by the dashboard stat cards and charts. */
export function summarize(submissions = []) {
  return {
    totalSubmissions: submissions.length,
    totalDocuments: sumDocuments(submissions),
    completed: submissions.filter((s) => inGroup(s, 'completed')).length,
    inProgress: submissions.filter((s) => inGroup(s, 'inProgress')).length,
    errorsRejected: submissions.filter((s) => inGroup(s, 'errorsRejected'))
      .length,
    byOverallStatus: bucketByOverallStatus(submissions),
    byPeppolStatus: bucketByApiStatus(submissions, 'peppolStatus'),
    byIrasStatus: bucketByApiStatus(submissions, 'irasStatus'),
    byRoute: bucketByRoute(submissions),
  }
}

/** UTC year-month key ('YYYY-MM') for a date-like value, or null if invalid. */
export function monthKey(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return null
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** Human label ('Sep 2025') for a 'YYYY-MM' key. */
export function formatMonthLabel(key) {
  if (!key || typeof key !== 'string') return ''
  const [year, month] = key.split('-')
  const idx = Number(month) - 1
  if (idx < 0 || idx > 11) return key
  return `${MONTH_ABBR[idx]} ${year}`
}

/** Inclusive list of 'YYYY-MM' keys between two date-like bounds (UTC). */
export function enumerateMonths(fromDate, toDate) {
  const start = fromDate instanceof Date ? fromDate : new Date(fromDate)
  const end = toDate instanceof Date ? toDate : new Date(toDate)
  const months = []
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return months
  let year = start.getUTCFullYear()
  let month = start.getUTCMonth()
  const endYear = end.getUTCFullYear()
  const endMonth = end.getUTCMonth()
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month + 1).padStart(2, '0')}`)
    month += 1
    if (month > 11) {
      month = 0
      year += 1
    }
  }
  return months
}

function emptyMonthlyRow(key) {
  return {
    month: key,
    totalSubmissions: 0,
    totalDocuments: 0,
    peppolTransmitted: 0,
    irasAccepted: 0,
    rejected: 0,
    pending: 0,
    routeC3C5: 0,
    routeC5: 0,
  }
}

/**
 * Monthly reporting figures (one row per month). When a { from, to } range is
 * provided, every month in the range is represented (zero-filled), which is what
 * a Peppol Authority monthly compliance table requires.
 */
export function bucketByMonth(submissions = [], range = {}) {
  const map = {}
  const ensure = (key) => {
    if (!map[key]) map[key] = emptyMonthlyRow(key)
    return map[key]
  }

  submissions.forEach((s) => {
    const key = monthKey(s && s.submissionDate)
    if (!key) return
    const row = ensure(key)
    const dc = docCountOf(s)
    const overall = s && s.statusOverall

    row.totalSubmissions += 1
    row.totalDocuments += dc
    if (s && s.peppolStatus === 'SUCC') row.peppolTransmitted += dc
    if (s && s.irasStatus === 'SUCC') row.irasAccepted += dc

    const isRejected =
      OVERALL_STATUS_GROUPS.errorsRejected.includes(overall) ||
      (s && s.peppolStatus === 'RJCT') ||
      (s && s.irasStatus === 'RJCT')
    if (isRejected) {
      row.rejected += dc
    } else if (OVERALL_STATUS_GROUPS.inProgress.includes(overall)) {
      row.pending += dc
    }

    if (s && s.destination === 'C3C5') row.routeC3C5 += dc
    else if (s && s.destination === 'C5') row.routeC5 += dc
  })

  let keys
  if (range && range.from && range.to) {
    keys = enumerateMonths(range.from, range.to)
    keys.forEach(ensure)
  } else {
    keys = Object.keys(map)
  }
  return keys.sort().map((key) => map[key])
}

/** Column totals across monthly rows. */
export function totalsOfMonthly(rows = []) {
  const totals = emptyMonthlyRow('total')
  delete totals.month
  rows.forEach((row) => {
    Object.keys(totals).forEach((key) => {
      totals[key] += num(row[key])
    })
  })
  return totals
}

/**
 * Reconciliation outcome: which submissions were successfully transmitted vs.
 * rejected/failed vs. still pending. Rejected and pending are the actionable
 * gaps that need attention/resubmission.
 */
export function reconcile(submissions = []) {
  const transmitted = []
  const rejected = []
  const pending = []

  submissions.forEach((s) => {
    const overall = s && s.statusOverall
    const isRejected =
      OVERALL_STATUS_GROUPS.errorsRejected.includes(overall) ||
      (s && s.peppolStatus === 'RJCT') ||
      (s && s.irasStatus === 'RJCT')
    if (isRejected) {
      rejected.push(s)
    } else if (overall === 'Completed') {
      transmitted.push(s)
    } else {
      pending.push(s)
    }
  })

  return {
    transmitted,
    rejected,
    pending,
    counts: {
      transmitted: transmitted.length,
      rejected: rejected.length,
      pending: pending.length,
      transmittedDocs: sumDocuments(transmitted),
      rejectedDocs: sumDocuments(rejected),
      pendingDocs: sumDocuments(pending),
      total: submissions.length,
      totalDocs: sumDocuments(submissions),
    },
  }
}
